import { ExclamationTriangleIcon, InformationCircleIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';

const backgrounds = {
    info: 'bg-blue-50 ',
    success: 'bg-green-50 ',
    warn: 'bg-yellow-50 ',
    error: 'bg-red-50 ',
};

const hover = {
    info: 'hover:text-blue-600',
    success: 'hover:text-green-600',
    warn: 'hover:text-yellow-600',
    error: 'hover:text-red-600',
};

const icons = {
    info: 'text-blue-400',
    success: 'text-green-400',
    warn: 'text-yellow-400',
    error: 'text-red-400',
};

const links = {
    info: 'text-blue-700 hover:text-blue-600',
    success: 'text-green-700 hover:text-green-600',
    warn: 'text-yellow-700 hover:text-yellow-60',
    error: 'text-red-700 hover:text-red-600',
};

const paragraphs = {
    info: 'text-blue-700',
    success: 'text-green-700',
    warn: 'text-yellow-700',
    error: 'text-red-700',
};

export type SimpleAlertProps = {
    type: 'info' | 'success' | 'warn' | 'error';
    title: string;

    link?: {
        text: string;
        to: string;
    };

    actionText?: {
        text: string;
        href: string;
        external?: boolean;
    };
};

/**
 * Alert component that only has text and an optional "see more" link.
 *
 */
function SimpleAlert({ type, title, link, actionText }: SimpleAlertProps): JSX.Element {
    const bgColor = backgrounds[type];
    const iconColor = icons[type];
    const hoverColor = hover[type];
    const linkColor = links[type];
    const paragraphColor = paragraphs[type];

    return (
        <div className={`rounded-md ${bgColor} p-4`}>
            <div className="flex">
                <div className="flex-shrink-0">
                    {type === 'info' && <InformationCircleIcon className={`h-5 w-5 ${iconColor}`} aria-hidden="true" />}
                    {type === 'success' && <CheckCircleIcon className={`h-5 w-5 ${iconColor}`} aria-hidden="true" />}
                    {type === 'warn' && <ExclamationTriangleIcon className={`h-5 w-5 ${iconColor}`} aria-hidden="true" />}
                    {type === 'error' && <XCircleIcon className={`h-5 w-5 ${iconColor}`} aria-hidden="true" />}
                </div>
                <div className="ml-3 flex-1 md:flex md:justify-between">
                    <p className={`text-sm ${paragraphColor}`}>
                        {title}
                        {actionText && (
                            <>
                                {' '}
                                {actionText.external ? (
                                    <a href={actionText.href} className={`font-medium ${paragraphColor} ${hoverColor} underline `}>
                                        {actionText.text}
                                    </a>
                                ) : (
                                    <Link to={actionText.href} className={`font-medium ${paragraphColor} ${hoverColor} underline `}>
                                        {actionText.text}
                                    </Link>
                                )}
                            </>
                        )}
                    </p>
                    {link && (
                        <p className="mt-3 text-sm md:mt-0 md:ml-6">
                            <a href={link.to} className={`whitespace-nowrap font-medium ${linkColor}`}>
                                {link.text} <span aria-hidden="true">&rarr;</span>
                            </a>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SimpleAlert;
